import type { ApiClient } from '@momonos/api-client'
import { formatBdPhone, formatBdt } from '@momonos/shared'
import { Badge, Card, Loader, useToast } from '@momonos/ui'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'

/**
 * FR-CRM-5 — customer search and profile.
 *
 * Staff read numbers back the way people say them ("ends in 678"), so search
 * matches suffixes as well as full numbers; the server normalizes spelling.
 */

interface CustomerRow {
  id: string
  phone: string | null
  name: string | null
  orderCount: number
  totalSpent: number
  lastOrderAt: number | null
  segments: string[]
}

interface Profile {
  customer: CustomerRow & {
    birthday: string | null
    firstOrderAt: number | null
    preferredSpiceLevel: number | null
  }
  timeline: Array<{
    id: string
    type: string
    refType: string | null
    data: Record<string, unknown> | null
    createdAt: number
  }>
  wallet: {
    points: number
    coupons: Array<{ id: string; code: string; type: string; value: number; source: string }>
  }
  consents: Array<{ channel: string; status: string }>
}

const SEGMENT_LABELS: Record<string, string> = {
  new: 'New',
  returning: 'Returning',
  vip: 'VIP',
  lost: 'Lost',
  high_spender: 'High spender',
  coupon_hunter: 'Coupon hunter',
  cheese_lover: 'Cheese lover',
  beef_lover: 'Beef lover',
}

/** The timeline is technical event names; staff read plain words. */
const EVENT_LABELS: Record<string, string> = {
  order_placed: 'Placed an order',
  order_completed: 'Order completed',
  order_refunded: 'Order refunded',
  points_earned: 'Earned points',
  points_redeemed: 'Redeemed points',
  points_expired: 'Points expired',
  coupon_granted: 'Received a coupon',
  coupon_redeemed: 'Used a coupon',
  referral_converted: 'Referred a friend',
  spin: 'Played the spinner',
  campaign_sent: 'Received a campaign',
  campaign_clicked: 'Opened a campaign',
  challenge_completed: 'Completed a challenge',
  consent_granted: 'Gave marketing consent',
  consent_revoked: 'Withdrew marketing consent',
}

export function CustomersScreen({ api }: { api: ApiClient; branchId: string | null }) {
  const { show } = useToast()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<CustomerRow[] | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)

  useEffect(() => {
    if (query.trim().length < 3) {
      setResults(null)
      return
    }
    let cancelled = false

    const timer = setTimeout(async () => {
      try {
        const result = await api.get<{ customers: CustomerRow[] }>('/v1/admin/customers', {
          query: { q: query.trim() },
        })
        if (!cancelled) setResults(result.customers)
      } catch {
        if (!cancelled) show('Could not search customers', { tone: 'error' })
      }
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [api, query, show])

  useEffect(() => {
    if (!selected) {
      setProfile(null)
      return
    }
    let cancelled = false
    api
      .get<Profile>(`/v1/admin/customers/${selected}`)
      .then((data) => {
        if (!cancelled) setProfile(data)
      })
      .catch(() => {
        if (!cancelled) show('Could not load the profile', { tone: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [api, selected, show])

  if (selected) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSelected(null)}
          className="flex items-center gap-2 text-content-secondary hover:text-content"
        >
          <ArrowLeft aria-hidden="true" className="size-4" /> All customers
        </button>

        {!profile ? (
          <Loader label="Loading profile" />
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-3">
              <h1 className="font-bold text-2xl text-content">
                {profile.customer.name ?? 'Unnamed customer'}
              </h1>
              {profile.customer.phone && (
                <span className="text-content-secondary tabular">
                  {formatBdPhone(profile.customer.phone)}
                </span>
              )}
              {profile.customer.segments.map((segment) => (
                <Badge key={segment} tone={segment === 'vip' ? 'yellow' : 'neutral'}>
                  {SEGMENT_LABELS[segment] ?? segment}
                </Badge>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-4">
              <Stat label="Visits" value={String(profile.customer.orderCount)} />
              <Stat label="Total spent" value={formatBdt(profile.customer.totalSpent)} />
              <Stat label="Points" value={String(profile.wallet.points)} />
              <Stat
                label="Last visit"
                value={
                  profile.customer.lastOrderAt
                    ? new Date(profile.customer.lastOrderAt).toLocaleDateString()
                    : '—'
                }
              />
            </div>

            {profile.wallet.coupons.length > 0 && (
              <Card>
                <h2 className="mb-2 font-semibold text-content">Coupons</h2>
                <ul className="space-y-1 text-sm">
                  {profile.wallet.coupons.map((coupon) => (
                    <li key={coupon.id} className="flex gap-3 text-content-secondary">
                      <span className="font-medium text-content tabular">{coupon.code}</span>
                      <span>
                        {coupon.type === 'percent'
                          ? `${coupon.value / 100}% off`
                          : formatBdt(coupon.value)}
                      </span>
                      <span className="text-content-muted">via {coupon.source}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <Card>
              <h2 className="mb-2 font-semibold text-content">Consent</h2>
              {profile.consents.length === 0 ? (
                <p className="text-content-muted text-sm">No marketing consent recorded.</p>
              ) : (
                <div className="flex gap-2">
                  {profile.consents.map((consent) => (
                    <Badge
                      key={consent.channel}
                      tone={consent.status === 'granted' ? 'green' : 'red'}
                    >
                      {consent.channel} {consent.status === 'granted' ? '✓' : '✕'}
                    </Badge>
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <h2 className="mb-2 font-semibold text-content">Timeline</h2>
              {profile.timeline.length === 0 ? (
                <p className="text-content-muted text-sm">Nothing yet.</p>
              ) : (
                <ul className="space-y-2">
                  {profile.timeline.map((event) => (
                    <li key={event.id} className="flex items-baseline gap-3 text-sm">
                      <span className="w-36 shrink-0 text-content-muted tabular">
                        {new Date(event.createdAt).toLocaleString()}
                      </span>
                      <span className="text-content">
                        {EVENT_LABELS[event.type] ?? event.type}
                        {typeof event.data?.orderNumber === 'string' &&
                          ` · ${event.data.orderNumber}`}
                        {typeof event.data?.total === 'number' &&
                          ` · ${formatBdt(event.data.total)}`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h1 className="font-bold text-2xl text-content">Customers</h1>

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by phone (full or last digits) or name"
        aria-label="Search customers"
        className="h-12 w-full max-w-md rounded-lg border border-surface-border bg-surface-raised px-3 text-content"
      />

      {query.trim().length < 3 ? (
        <p className="text-content-muted">Type at least three characters to search.</p>
      ) : !results ? (
        <Loader label="Searching" />
      ) : results.length === 0 ? (
        <p className="text-content-muted">No customers match.</p>
      ) : (
        <div className="grid gap-3">
          {results.map((customer) => (
            <button key={customer.id} type="button" onClick={() => setSelected(customer.id)}>
              <Card className="flex items-center gap-4 text-left hover:border-surface-border-strong">
                <div className="flex-1">
                  <p className="font-semibold text-content">
                    {customer.name ?? 'Unnamed customer'}
                  </p>
                  <p className="text-content-muted text-sm tabular">
                    {customer.phone ? formatBdPhone(customer.phone) : 'No phone'}
                  </p>
                </div>
                <div className="flex gap-1">
                  {customer.segments.slice(0, 3).map((segment) => (
                    <Badge key={segment} tone="neutral">
                      {SEGMENT_LABELS[segment] ?? segment}
                    </Badge>
                  ))}
                </div>
                <span className="w-20 text-right text-content-secondary text-sm">
                  {customer.orderCount} visits
                </span>
                <span className="w-24 text-right font-medium text-content tabular">
                  {formatBdt(customer.totalSpent)}
                </span>
              </Card>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <p className="text-content-muted text-xs uppercase tracking-wide">{label}</p>
      <p className="mt-1 font-bold text-content text-xl tabular">{value}</p>
    </Card>
  )
}
